import {TaskBoardColumn} from "../../api/Tasks2";
import {css} from "@emotion/css";
import {grey} from "@mui/material/colors";
import {useAsync2} from "nate-react-api-helpers";
import {api} from "../../api/API";
import {Card} from "./Card";
import {BoardContext} from "./BoardContextProvider";
import {useContext, useEffect} from "react";
import {useSyncedRef} from "../../misc/SyncedRef";
import {AutomationIcon, CompleteIcon} from "./CompleteIcon";
import {CircularProgress, Tooltip} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    Draggable,
    DraggableProvided, DraggableStateSnapshot,
    Droppable,
    DroppableProvided,
} from "react-beautiful-dnd";
import {ComposeCard} from "./ComposeCard";

export function BoardColumnEl(props: {
    value: TaskBoardColumn;
    index: number;
}) {

    const cardList = useAsync2(input => api.tasks2.getBoardColumnContent(input),
        {column: props.value.id}, [props.value.id]);

    const cardListReloadRef = useSyncedRef(cardList.reload);
    const {refreshColumnsEmitter, onShowColumn, columnLoadPending, columnReloadEmitter} = useContext(BoardContext);

    useEffect(() => {
        const sub = refreshColumnsEmitter.subscribe(() => cardListReloadRef.current());
        return () => sub.cancel()
    }, [refreshColumnsEmitter, cardListReloadRef])

    const id = props.value.id;
    useEffect(() => {
        const sub = columnReloadEmitter.subscribe((v) => {
            if(v === id) {
                cardListReloadRef.current()
            }
        });

        return () => sub.cancel()
    }, [id, columnReloadEmitter, cardListReloadRef])

    return (
        <Draggable draggableId={"column:" + props.value.id} index={props.index}>
            {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
            <div className={columnCls} ref={provided.innerRef} {...provided.draggableProps}>
                <div style={{
                    padding: 6,
                    paddingLeft: 12,
                    paddingRight: 12,
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    color: grey["700"],
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                }}>
                    <div>
                        <Tooltip title="Drag and drop to re-order">
                            <div style={{
                                position: "relative",
                                top: 2,
                            }}
                                 {...provided.dragHandleProps}
                            >
                                <DragIndicatorIcon fontSize="inherit" />
                            </div>
                        </Tooltip>
                    </div>

                    <button className={titleCss} onClick={() => onShowColumn(props.value)}>
                        {props.value.title}
                    </button>
                    {props.value.completedColumn && <div>
                        <Tooltip title="Items placed in this column are automatically marked as completed">
                            <div>
                                <CompleteIcon mode="column" />
                            </div>
                        </Tooltip>
                    </div>}
                    {Object.keys(props.value.autoAddRule || {}).length > 0 && <div>
                        <Tooltip title="Special rules will auto-add tasks to this column">
                            <div>
                                <AutomationIcon mode="column" />
                            </div>
                        </Tooltip>
                    </div>}
                </div>
                <div style={{
                    paddingLeft: 8,
                    paddingRight: 8,
                    paddingBottom: 8,

                    // backgroundColor: snapshot.isDraggingOver ? "hsla(0, 100%, 100%, 0.1)" : "transparent",
                    transition: 'background-color 0.2s ease',
                    position: "relative",
                    overflowY: "auto",
                    flex: 1,
                }}>
                    <Droppable
                        droppableId={"drag-column:" + props.value.id}
                        isCombineEnabled={false}
                        type="COLUMN"
                        direction="vertical"

                    >
                        {(provided: DroppableProvided) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                <div style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 6,
                                    minHeight: 300,
                                }}>
                                    {cardList.result?.map((card, index) => (
                                        <Draggable key={card.id} draggableId={"card:" + card.id} index={index}>
                                            {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                                <Card provided={provided} key={card.id} value={card} column={props.value} />
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            </div>)}
                    </Droppable>
                </div>
                {cardList.LoadingOrErrorElement}
                {columnLoadPending.includes(id) ? <CircularProgress /> : null}
                <ComposeCard column={props.value.id} />
            </div>
            )}
        </Draggable>
    )
}

const titleCss = css({
    flex: 1,
    cursor: "pointer",
    border: "none",
    textAlign: "left",
    padding: 0,

    "&:hover": {
        textDecoration: "underline",
    }
})

export const columnCls = css({
    borderRight: "1px solid " + grey["300"],
    borderTop: "1px solid " + grey["300"],
    width: 275,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
})